import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldTextarea,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

// /api/customer-notes REST controller'ı (OData DEĞİL) — ApiResponse<{items,...}> / ApiResponse<CustomerNote> döner.
// Saha notları yalnızca mobile'dan oluşturulur; admin yalnızca görüntüler + içerik/durum günceller + siler.
// Bu yüzden bu detay ekranı "Yeni" oluşturmayı desteklemez (POST yok) — yalnızca düzenleme/görüntüleme.
const API_BASE = '/api/customer-notes';

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Açık' },
  { value: 'DONE', label: 'Tamamlandı' },
  { value: 'ARCHIVED', label: 'Arşiv' },
];

const STATUS_META = {
  OPEN: { label: 'Açık', tone: 'wait' },
  DONE: { label: 'Tamamlandı', tone: 'ok' },
  ARCHIVED: { label: 'Arşiv', tone: 'neutral' },
};

const EMPTY_FORM = {
  customerId: '',
  customerTitle: '',
  title: '',
  description: '',
  status: 'OPEN',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function rowToForm(r) {
  return {
    customerId: pick(r, 'customerId', 'CustomerId') ?? '',
    customerTitle: pick(r, 'customerTitle', 'CustomerTitle') ?? '',
    title: pick(r, 'title', 'Title') ?? '',
    description: pick(r, 'description', 'Description') ?? '',
    status: pick(r, 'status', 'Status') ?? 'OPEN',
  };
}

export function CustomerNoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Saha Notu' : 'Saha Notu Detayı', 'Saha temsilcisinin kişisel notu (admin görüntüleme/düzenleme).');

  const [form, setForm] = useState(EMPTY_FORM);
  const [audit, setAudit] = useState(null);
  // Sahibi (temsilci) — değiştirilemez bilgi; listeden/GetById'den taşınır.
  const [representativeName, setRepresentativeName] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    try {
      // 1) Önce listeden taşınan satır (navigate state) — temsilci adı + müşteri başlığı burada gelir.
      const stateRow = location.state?.row;
      if (stateRow) {
        setForm(rowToForm(stateRow));
        setRepresentativeName(pick(stateRow, 'representativeName', 'RepresentativeName') ?? '');
        // Liste satırı (OData $select) audit alanlarını içermez → tam kaydı arka planda çek (bloklamaz).
        fetch(`${API_BASE}/${id}`, { headers: authHeaders() })
          .then((res) => (res.ok ? res.json() : null))
          .then((body) => { const rec = unwrap(body); if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') }); })
          .catch(() => {});
        setLoading(false);
        return;
      }
      // 2) Doğrudan URL ile gelindiyse GetById ile çek (ham entity — temsilci/müşteri adı içermez).
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Not alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      setForm(rowToForm(r));
      setRepresentativeName(pick(r, 'representativeName', 'RepresentativeName') ?? '');
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Not alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, location.state]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /customer-notes/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  // location.key her navigasyonda değiştiği için bir klon yalnızca bir kez uygulanır.
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setRepresentativeName('');
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildBody = () => ({
    customerId: form.customerId.trim() || null,
    title: form.title.trim(),
    description: form.description.trim() || null,
    status: form.status,
  });

  // Saha notları mobile'dan oluşturulur — backend'de POST yoktur. Bu yüzden persist yalnızca
  // düzenleme modunda (PUT) çalışır; andNew parametresi PersonnelDetailPage deseniyle uyum için durur.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.title.trim()) { showToast('error', 'Başlık zorunludur.'); return; }
    if (isNew) {
      showToast('error', 'Saha notu yalnızca mobile uygulamadan oluşturulabilir.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Kayıt başarısız (${res.status})`);
      showToast('success', 'Not güncellendi.');
      setTimeout(() => navigate('/customer-notes'), 400);
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın form bilgilerini taşıyarak yeni-kayıt ekranını aç.
  // Hiçbir şey kaydedilmez (saha notu zaten yalnızca mobile'dan oluşturulur).
  const handleClone = () => {
    navigate('/customer-notes/new', { state: { clone: { ...form, customerTitle: '' } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Not silindi.');
      setTimeout(() => navigate('/customer-notes'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const meta = STATUS_META[form.status] || { label: form.status || '-', tone: 'neutral' };
  const statusBadge = !isNew && <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/customer-notes')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni not aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Saha Notları', to: '/customer-notes' }, { label: isNew ? 'Yeni Saha Notu' : 'Saha Notu Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'CustomerNote', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section
          icon="list"
          title="Not Bilgileri"
          subtitle="Başlık, açıklama ve durum"
          note={isNew
            ? 'Saha notları yalnızca mobile uygulamadan oluşturulur. Bu ekranda yeni not kaydedilemez.'
            : `Sahibi: ${representativeName || '—'} (değiştirilemez). Yeni not yaratma sadece mobile'dan; admin içerik/durum günceller veya siler.`}
        >
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="title" label="Başlık" req span />
            <FieldText
              form={form} set={set} k="customerId" label="Müşteri ID (GUID — opsiyonel)" mono
              placeholder="(genel not için boş bırakın)" span
            />
            {form.customerTitle && (
              <div className="dps-ff dps-span2" style={{ fontSize: 12, color: '#16a34a', marginTop: -8 }}>
                ✓ {form.customerTitle}
              </div>
            )}
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS} req />
            <FieldTextarea form={form} set={set} k="description" label="Açıklama" rows={5} span />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.title}”</strong> notunu silmek istediğinize emin misiniz?
          </p>
          <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
            Mobile bu notu bir sonraki sync'te artık görmeyecek.
          </div>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default CustomerNoteDetailPage;
